import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search, Receipt, Trash2, CheckCircle2, Ban, RotateCcw, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { CAT_MAP, EXPENSE_CATEGORIES, STATUS_MAP_EXP, SPLIT_LABELS, fmtMoney } from "@/lib/expenseConstants";
import { SpaceSection } from "@/components/SpaceSection";
import { ExpenseDialog } from "@/components/expenses/ExpenseDialog";
import { BalancesTab } from "@/components/expenses/BalancesTab";
import { BudgetsTab } from "@/components/expenses/BudgetsTab";
import { ReportTab } from "@/components/expenses/ReportTab";
import { UserAvatar } from "@/components/Avatars";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const ExpenseDetail = ({ expense, onClose, workspaceId, myId, isAdmin }) => {
  const qc = useQueryClient();
  if (!expense) return null;
  const canManage = expense.created_by === myId || expense.payer_id === myId || isAdmin;
  const status = STATUS_MAP_EXP[expense.status];

  const act = async (fn, msg) => {
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ["expenses", workspaceId] });
      qc.invalidateQueries({ queryKey: ["balances", workspaceId] });
      qc.invalidateQueries({ queryKey: ["budgets", workspaceId] });
      toast.success(msg);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Sheet open={!!expense} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="expense-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading">{CAT_MAP[expense.category]?.emoji} {expense.concept}</SheetTitle>
          <SheetDescription>
            {expense.entry_type === "income" ? "Ingreso" : "Gasto"} · {expense.date} · registrado por {expense.created_by_name} · {timeAgo(expense.created_at)}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-5 mt-4">
          <div className="flex items-center gap-3">
            <span className={`font-heading text-3xl font-bold ${expense.entry_type === "income" ? "text-emerald-600 dark:text-emerald-400" : ""}`}>
              {expense.entry_type === "income" ? "+" : ""}{fmtMoney(expense.amount, expense.currency)}
            </span>
            <Badge style={{ backgroundColor: `${status?.color}22`, color: status?.color }} data-testid="expense-status-badge">
              {status?.label}
            </Badge>
          </div>
          {expense.description && <p className="text-sm text-muted-foreground">{expense.description}</p>}
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              {expense.entry_type === "income" ? `Recibió ${expense.payer_name}` : `Pagó ${expense.payer_name} · ${SPLIT_LABELS[expense.split_method]}`}
            </p>
            {expense.participants.map((p) => (
              <div key={p.profile_id} className="flex items-center gap-2 text-sm" data-testid={`expense-share-${p.profile_id}`}>
                <UserAvatar name={p.name} color={p.avatar_color} size="sm" className="!h-6 !w-6 !text-[10px]" />
                <span className="flex-1">{p.name}</span>
                <span className="font-medium">{fmtMoney(p.share, expense.currency)}</span>
              </div>
            ))}
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2 pt-3 border-t">
              {expense.status === "pending" && (
                <Button size="sm" variant="outline" data-testid="expense-mark-settled-btn"
                  onClick={() => act(() => api.put(`/expenses/${expense.id}`, { status: "settled" }), "Marcado como liquidado")}>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Marcar liquidado
                </Button>
              )}
              {expense.status === "pending" && (
                <Button size="sm" variant="outline" data-testid="expense-cancel-btn"
                  onClick={() => act(() => api.put(`/expenses/${expense.id}`, { status: "cancelled" }), "Gasto anulado")}>
                  <Ban className="h-3.5 w-3.5 mr-1" /> Anular
                </Button>
              )}
              {expense.status !== "pending" && (
                <Button size="sm" variant="outline" data-testid="expense-reopen-btn"
                  onClick={() => act(() => api.put(`/expenses/${expense.id}`, { status: "pending" }), "Gasto reabierto")}>
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reabrir
                </Button>
              )}
              <Button size="sm" variant="ghost" className="text-destructive" data-testid="expense-delete-btn"
                onClick={() => { act(() => api.delete(`/expenses/${expense.id}`), "Gasto eliminado"); onClose(); }}>
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Eliminar
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function ExpensesPage() {
  const { id: workspaceId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "expenses";
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [person, setPerson] = useState("all");
  const [entryType, setEntryType] = useState("all");

  useEffect(() => {
    if (params.get("new")) setDialogOpen(true);
  }, [params]);

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: async () => (await api.get("/auth/me")).data });
  const { data: ws } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}`)).data,
  });
  const { data: members = [] } = useQuery({
    queryKey: ["members", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/members`)).data,
  });
  const { data: projects = [] } = useQuery({
    queryKey: ["projects", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/projects`)).data,
  });
  const { data: expenses = [] } = useQuery({
    queryKey: ["expenses", workspaceId, q, status, category, person, entryType],
    queryFn: async () => {
      const p = {};
      if (q) p.q = q;
      if (status !== "all") p.status = status;
      if (category !== "all") p.category = category;
      if (person !== "all") p.participant_id = person;
      if (entryType !== "all") p.entry_type = entryType;
      return (await api.get(`/workspaces/${workspaceId}/expenses`, { params: p })).data;
    },
  });

  const selected = expenses.find((e) => e.id === selectedId);
  const isAdmin = ["owner", "admin"].includes(ws?.my_role);
  const totalPending = expenses.filter((e) => e.status === "pending" && e.entry_type !== "income").reduce((s, e) => s + e.amount, 0);

  return (
    <SpaceSection title="Cuentas (€)" actions={
      <>
        <Button size="sm" variant="outline" data-testid="expenses-export-btn" title="Exportar CSV"
          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${workspaceId}/expenses-export`, "_blank")}>
          <Download className="h-4 w-4" />
        </Button>
        <Button size="sm" data-testid="expense-create-btn" onClick={() => setDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Registrar
        </Button>
      </>
    }>
      <div className="flex gap-1.5 overflow-x-auto">
        {[["expenses", "Movimientos"], ["balances", "Balances"], ["budgets", "Presupuestos"], ["report", "Informe"]].map(([v, l]) => (
          <button key={v} onClick={() => setParams({ tab: v })} data-testid={`expenses-tab-${v}`}
            className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors ${tab === v ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "expenses" && (
        <>
          <div className="flex flex-wrap items-center gap-2" data-testid="expenses-filter-bar">
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="expenses-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por concepto..." className="pl-8 h-8 w-48 text-sm" />
            </div>
            <Select value={entryType} onValueChange={setEntryType}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="expenses-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todo tipo</SelectItem>
                <SelectItem value="expense">Gastos</SelectItem>
                <SelectItem value="income">Ingresos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="expenses-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendientes</SelectItem>
                <SelectItem value="settled">Liquidados</SelectItem>
                <SelectItem value="cancelled">Anulados</SelectItem>
              </SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="expenses-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toda categoría</SelectItem>
                {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.emoji} {c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={person} onValueChange={setPerson}>
              <SelectTrigger className="h-8 w-40 text-xs" data-testid="expenses-person-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las personas</SelectItem>
                {members.map((m) => <SelectItem key={m.profile_id} value={m.profile_id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {expenses.length > 0 && (
              <span className="text-xs text-muted-foreground ml-auto" data-testid="expenses-total">
                Pendiente: <b className="text-foreground">{fmtMoney(totalPending)}</b>
              </span>
            )}
          </div>

          {expenses.length === 0 ? (
            <Card className="p-12 text-center border-dashed">
              <Receipt className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">Registra tu primer gasto en diez segundos.</p>
            </Card>
          ) : (
            <Card className="divide-y divide-border" data-testid="expenses-list">
              {expenses.map((e) => {
                const st = STATUS_MAP_EXP[e.status];
                const proj = projects.find((p) => p.id === e.project_id);
                const isIncome = e.entry_type === "income";
                return (
                  <div key={e.id} className="p-3.5 flex items-center gap-3 hover:bg-secondary/50 cursor-pointer"
                    onClick={() => setSelectedId(e.id)} data-testid={`expense-row-${e.id}`}>
                    <span className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center text-base shrink-0">
                      {CAT_MAP[e.category]?.emoji}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{e.concept}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {e.date} · {isIncome ? `recibió ${e.payer_name}` : `pagó ${e.payer_name} · ${e.participants.length} ${e.participants.length === 1 ? "persona" : "personas"}`}
                        {proj && ` · ${proj.icon} ${proj.name}`}
                      </p>
                    </div>
                    <span className={`font-heading font-semibold shrink-0 ${isIncome ? "text-emerald-600 dark:text-emerald-400" : ""}`}>
                      {isIncome ? "+" : ""}{fmtMoney(e.amount, e.currency)}
                    </span>
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: st?.color }} title={st?.label} />
                  </div>
                );
              })}
            </Card>
          )}
        </>
      )}

      {tab === "balances" && <BalancesTab workspaceId={workspaceId} />}
      {tab === "budgets" && <BudgetsTab workspaceId={workspaceId} projects={projects} />}
      {tab === "report" && <ReportTab workspaceId={workspaceId} />}

      <ExpenseDialog workspaceId={workspaceId} members={members} projects={projects}
        myId={me?.id} open={dialogOpen} onOpenChange={setDialogOpen} />
      <ExpenseDetail expense={selected} onClose={() => setSelectedId(null)}
        workspaceId={workspaceId} myId={me?.id} isAdmin={isAdmin} />
    </SpaceSection>
  );
}
